import type { AssetInstructionOut } from "kubb";
import { useEffect, useState } from "react";

/**
 * Formats instruction type enum to display string.
 * e.g., "full" -> "Full", "device_list" -> "Device List", "get_configuration" -> "Get Configuration"
 *
 * Factory reset workflow steps:
 * - "factory_reset" -> "Factory Reset"
 * - "setup_apn" -> "Setup APN"
 * - "setup_dns" -> "Setup DNS"
 * - "sms_configuration" -> "SMS Configuration"
 * - "configuration" -> "Configuration" (full telnet config)
 */
export function formatInstructionType(type: string): string {
  const typeMap: Record<string, string> = {
    full: "Full",
    basic: "Basic",
    device_list: "Device List",
    adhoc_report_schedule: "Adhoc Report Schedule",
    adhoc_report_execute: "Adhoc Report Execute",
    get_configuration: "Get Configuration",
    // Factory reset workflow step types
    factory_reset: "Factory Reset",
    setup_apn: "Setup APN",
    setup_dns: "Setup DNS",
    sms_configuration: "SMS Configuration",
    configuration: "Configuration",
    // New enum-style types (SCREAMING_CASE)
    SYNC_CONFIG: "Sync Config",
    READ_CONFIG: "Read Config",
    DEVICE_LIST: "Device List",
    FACTORY_RESET: "Factory Reset",
    SETUP_APN: "Setup APN",
    SETUP_DNS: "Setup DNS",
    TIMESYNC: "Time Sync",
    SWITCH_RELAY: "Switch Relay",
    MBUS_SEARCH: "MBus Search",
    FIRMWARE_UPGRADE: "Firmware Upgrade",
    GET_STATUS: "Get Status",
    SETUP_WIRELESS_RECEIVERS: "Setup Wireless Receivers",
    CLEAR_DEVICE_LIST: "Clear Device List",
  };
  return typeMap[type] || type;
}

/**
 * Formats trigger enum to display string.
 * e.g., "manual" -> "Manual", "scheduled_sync" -> "Scheduled Sync"
 */
export function formatTrigger(trigger: string): string {
  const triggerMap: Record<string, string> = {
    manual: "Manual",
    scheduled_sync: "Scheduled Sync",
    drift_detection: "Drift Detection",
    factory_reset: "Factory Reset",
    asset_settings_update: "Asset Settings Update",
  };
  return triggerMap[trigger] || trigger;
}

/**
 * Returns CSS classes for status badge styling.
 * success=green, failed=red, pending=gray, in_progress=blue, cancelled=yellow, pending_retry=orange
 */
export function getStatusClasses(status: string): string {
  const baseClasses =
    "inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-medium min-w-[100px]";
  const statusClasses: Record<string, string> = {
    success:
      "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
    failed: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    in_progress:
      "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
    cancelled:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
    pending_retry:
      "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  };
  return `${baseClasses} ${statusClasses[status] || "bg-gray-100 text-gray-700"}`;
}

/**
 * Formats status enum to display string.
 * e.g., "in_progress" -> "In Progress"
 */
export function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: "Pending",
    in_progress: "In Progress",
    pending_retry: "Pending Retry",
    success: "Success",
    failed: "Failed",
    cancelled: "Cancelled",
  };
  return statusMap[status] || status;
}

/**
 * Formats configuration method enum to display string.
 * e.g., "telnet" -> "Telnet", "sms" -> "SMS", "mbus" -> "MBus"
 */
export function formatMethod(method: string | undefined | null): string {
  if (!method) return "-";
  const methodMap: Record<string, string> = {
    telnet: "Telnet",
    sms: "SMS",
    mbus: "MBus",
    lwm2m: "LWM2M",
    mqtt: "MQTT",
  };
  return methodMap[method] || method;
}

/**
 * Returns sync type display info for instructions with sync config options.
 * Reads from options.all_configuration for sync type information.
 *
 * @param instruction - The instruction to check
 * @returns Display info with label and CSS classes, or null if not applicable
 */
export function getSyncTypeInfo(
  instruction: AssetInstructionOut,
): { label: string; classes: string } | null {
  // Only show sync type for SYNC_CONFIG or full/basic instruction types
  const syncTypes = ["configuration", "full", "basic", "SYNC_CONFIG"];
  if (!syncTypes.includes(instruction.instruction_type)) {
    return null;
  }

  // Read from options.all_configuration for sync type info
  if (instruction.options?.type === "sync_config") {
    const allConfig = instruction.options.all_configuration;
    if (allConfig === true) {
      return {
        label: "Full",
        classes: "bg-blue-50 text-blue-500 dark:bg-blue-950 dark:text-blue-400",
      };
    }
    if (allConfig === false) {
      return {
        label: "Targeted",
        classes: "bg-cyan-50 text-cyan-500 dark:bg-cyan-950 dark:text-cyan-400",
      };
    }
  }

  return null;
}

/**
 * Returns relay action display info for SWITCH_RELAY instructions.
 * Shows "Turn On" (green) or "Turn Off" (red) based on the relay_action option.
 *
 * @param instruction - The instruction to check
 * @returns Display info with label, CSS classes, and tooltip, or null if not applicable
 */
export function getRelayActionInfo(
  instruction: AssetInstructionOut,
): { label: string; classes: string; tooltip: string } | null {
  // Only show for SWITCH_RELAY instructions
  if (instruction.instruction_type !== "SWITCH_RELAY") {
    return null;
  }

  // Check if options has relay_action type
  if (instruction.options?.type !== "relay_action") {
    return null;
  }

  const relayAction = instruction.options.relay_action;

  if (relayAction === "ON") {
    return {
      label: "On",
      classes:
        "bg-green-50 text-green-500 dark:bg-green-950 dark:text-green-400",
      tooltip: "Relay action: Turn relay ON",
    };
  }

  if (relayAction === "OFF") {
    return {
      label: "Off",
      classes: "bg-red-50 text-red-500 dark:bg-red-950 dark:text-red-400",
      tooltip: "Relay action: Turn relay OFF",
    };
  }

  return null;
}

/**
 * Hook to format a scheduled_at datetime as a live countdown.
 * Updates every second when within 10 minutes of the scheduled time.
 */
export function useCountdown(
  scheduledAt: string | null | undefined,
): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!scheduledAt) return;

    const scheduled = new Date(scheduledAt).getTime();
    const diffMs = scheduled - now;

    // Only update if within 10 minutes and in the future
    if (diffMs > 0 && diffMs < 10 * 60 * 1000) {
      const interval = setInterval(() => {
        setNow(Date.now());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [scheduledAt, now]);

  if (!scheduledAt) return null;

  const scheduled = new Date(scheduledAt).getTime();
  const diffMs = scheduled - now;
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds <= 0) {
    return "Starting...";
  }

  if (diffSeconds < 60) {
    return `in ${diffSeconds}s`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 10) {
    const remainingSeconds = diffSeconds % 60;
    return `in ${diffMinutes}m ${remainingSeconds}s`;
  }

  // Show time for longer delays
  return new Date(scheduledAt).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Returns true if countdown should be shown for the given status.
 * Countdown is only relevant for pending or pending_retry statuses,
 * and not for LWM2M instructions where scheduled_at is a cooldown
 * rather than an expected execution time.
 */
export function shouldShowCountdown(
  status: string,
  configurationMethod?: string | null,
): boolean {
  if (configurationMethod === "lwm2m") return false;
  return status === "pending" || status === "pending_retry";
}

/**
 * Returns true if an LWM2M instruction is waiting for the device to wake up.
 * LWM2M devices operate in queue mode — instructions execute when
 * the device wakes up, which is unpredictable.
 */
export function isAwaitingDevice(
  status: string,
  configurationMethod?: string | null,
): boolean {
  if (configurationMethod !== "lwm2m") return false;
  return status === "pending" || status === "pending_retry";
}
